import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface Profile {
  id: string;
  role: 'ogretmen' | 'ogrenci';
  full_name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  created_at?: string;
}

export interface StudentSignup {
  fullName: string;
  email: string;
  password: string;
  phone: string;
  level: string;
}

interface SignInResult {
  error: string | null;
}

interface SignUpResult {
  error: string | null;
  needsEmailConfirm: boolean;
}

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  isTeacher: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signUpStudent: (data: StudentSignup) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        const nextUser = data?.session?.user ?? null;
        // Aynı kullanıcı farklı referansla geliyorsa state değişmesin,
        // aksi halde gereksiz profil yeniden çekilip AuthGuard yönlendirir.
        setUser((prev) => {
          if (prev && nextUser && prev.id === nextUser.id) return prev;
          return nextUser;
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
      });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        return;
      }
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') {
        const nextUser = session?.user ?? null;
        setUser((prev) => {
          if (prev && nextUser && prev.id === nextUser.id) return prev;
          return nextUser;
        });
      }
      // Token yenileme (TOKEN_REFRESHED) olayında user state'ini değiştirme.
      // Aksi halde her yenilemede yeni user referansı oluşur, profil tekrar çekilir
      // ve bu yarış durumu profilin anlık null olmasına / panelin giriş ekranına atmasına yol açar.
    });

    return () => {
      cancelled = true;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);
    supabase
      .from('zehrahoca_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) {
          setProfile((data as Profile) ?? null);
          setProfileLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProfileLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  }, []);

  const signUpStudent = useCallback(async (data: StudentSignup): Promise<SignUpResult> => {
    const { data: authData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          full_name: data.fullName,
          phone: data.phone,
        },
      },
    });

    if (error) return { error: error.message, needsEmailConfirm: false };
    if (!authData.user) return { error: 'Kayıt tamamlanamadı, lütfen tekrar deneyin.', needsEmailConfirm: false };

    if (!authData.session) {
      return { error: null, needsEmailConfirm: true };
    }

    const { error: profileError } = await supabase.from('zehrahoca_profiles').insert({
      id: authData.user.id,
      role: 'ogrenci',
      full_name: data.fullName,
      email: data.email,
      phone: data.phone,
    });

    if (profileError) return { error: profileError.message, needsEmailConfirm: false };

    const { error: studentError } = await supabase.from('zehrahoca_students').insert({
      id: authData.user.id,
      level: data.level,
      register_date: new Date().toISOString().slice(0, 10),
    });

    if (studentError) return { error: studentError.message, needsEmailConfirm: false };

    return { error: null, needsEmailConfirm: false };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    profileLoading,
    isTeacher: profile?.role === 'ogretmen',
    signIn,
    signUpStudent,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}